// src/utils/exportShotsCsv.js
import { translate } from "./i18n";
import { displayTagValue } from "./shotTags";
import { getShotDisplayNumber } from "./shotNumber";
import { getSceneNumberLabel } from "./parseScenes";
import {
  effectiveFrameFormatValue,
  effectiveColorFormatValue,
  getShotFrameFormat,
  getShotColorFormat,
} from "./frameFormats";

function inferImageInfo(shot) {
  const img = shot?.image;
  const dataUrl = typeof img === "string" ? img : img?.dataUrl || "";
  const type =
    typeof img === "object" && img?.type
      ? img.type
      : dataUrl.startsWith("data:")
      ? dataUrl.slice(5, dataUrl.indexOf(";"))
      : "";
  return { dataUrl, type };
}

function extFromMime(mime) {
  const m = (mime || "").toLowerCase();
  if (m.includes("png")) return "png";
  if (m.includes("jpeg") || m.includes("jpg")) return "jpg";
  if (m.includes("webp")) return "webp";
  return "png";
}

function sanitizePart(s) {
  return String(s || "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_-]/g, "");
}

function makeShotImageFilename(sceneIdx, shotNumber, mime) {
  if (!shotNumber) shotNumber = "S0";
  const sc = String(sceneIdx || 0).padStart(2, "0");
  const sn = sanitizePart(shotNumber);
  const ext = extFromMime(mime);
  return `SC${sc}_${sn}.${ext}`;
}

function safeInt(n, fallback = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? x : fallback;
}

function csvCell(value, delimiter) {
  if (value === null || value === undefined) return "";
  let s = String(value);
  if (s.includes('"')) s = s.replace(/"/g, '""');
  const mustQuote =
    s.includes("\n") || s.includes("\r") || s.includes('"') || s.includes(delimiter);
  return mustQuote ? `"${s}"` : s;
}

function parseSceneHeadingParts(sceneTitle) {
  const t0 = (sceneTitle || "").trim();
  if (!t0) return { intExt: "", location: "", timeOfDay: "" };

  // remove optional prefixes "SC1." / "SCENE 1"
  const t = t0
    .replace(/^SC\s*\d+\s*[.:\-]?\s*/i, "")
    .replace(/^(SCENE|SCÈNE)\s*\d+\s*[.:\-]?\s*/i, "")
    .trim();

  const m = t.match(
    /^(INT\.?\/EXT\.?|INT\/EXT|I\/E|INT\.?|EXT\.?|INT|EXT)\b\.?\s*(.*)$/i
  );

  const intExt = (m?.[1] || "").replace(/\./g, "").toUpperCase();
  const rest = (m?.[2] || t).trim();

  // "<LOCATION> - <TIME OF DAY>"
  const parts = rest.split(/\s*-\s*/).filter(Boolean);
  if (parts.length <= 1) return { intExt, location: rest, timeOfDay: "" };

  const timeOfDay = parts[parts.length - 1].trim();
  const location = parts.slice(0, -1).join(" - ").trim();
  return { intExt, location, timeOfDay };
}

/**
 * Export CSV (Excel + Notion)
 * - default delimiter ; (often best for Excel FR/CH)
 * - UTF-8 BOM for accents
 */
export function buildShotsCsv({
  shots,
  scenes,
  scriptText,
  lang = "en",
  shotPrefixMode,
  shotFrameFormat,
  delimiter = ";",
  includeBOM = true,
}) {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const filename = `shotlist_shots_${y}-${m}-${d}.csv`;

  const t = (key, vars) => translate(lang, key, vars);

  const sceneIndexById = new Map();
  const sceneLabelById = new Map();
  (scenes || []).forEach((s, idx) => {
    sceneIndexById.set(s.id, idx + 1);
    sceneLabelById.set(s.id, getSceneNumberLabel(s, idx));
  });

  const rows = [];
  // ✅ headers translated
  rows.push([
    t("export.col.sceneIndex"),
    t("export.col.shotNumber"),
    t("export.col.intExt"),
    t("export.col.location"),
    t("export.col.timeOfDay"),
    t("export.col.shotSize"),
    t("export.col.angle"),
    t("export.col.lensMm"),
    t("export.col.focus"),
    t("export.col.framing"),
    t("export.col.movement"),
    t("export.col.lights"),
    t("export.col.durationSec"),
    t("export.col.description"),
    t("export.col.ratio"),
    t("export.col.colorFormat"),
    t("export.col.image"),
    t("export.col.scriptText"),
    t("export.col.master"),
  ]);

  const decorated = (shots || []).map((sh) => {
    const sceneIdx = sceneIndexById.get(sh.sceneId) ?? 0;
    const scene = (scenes || []).find((s) => s.id === sh.sceneId) || null;

    const start = safeInt(sh.scriptRange?.start, 0);
    const end = safeInt(sh.scriptRange?.end, 0);
    const a = Math.max(0, Math.min(start, end));
    const b = Math.max(0, Math.max(start, end));

    const raw = (scriptText || "").slice(a, b);

    const sceneLabel = sceneLabelById.get(sh.sceneId) ?? String(sceneIdx);
    // ✅ ordre = ordre d'affichage (listOrder), sinon numérotation, sinon position texte
    const sortKey = Number.isFinite(sh.listOrder)
      ? sh.listOrder
      : Number.isFinite(sh.order)
      ? sh.order
      : a;
    return { shot: sh, scene, sceneIdx, sceneLabel, start: a, sortKey, raw };
  });

  // sort by scene then by display order
  decorated.sort((a, b) =>
    a.sceneIdx !== b.sceneIdx ? a.sceneIdx - b.sceneIdx : a.sortKey - b.sortKey
  );

  for (const it of decorated) {
    const sh = it.shot;
    const shotDisplayNumber = getShotDisplayNumber(sh.shotNumber, lang, shotPrefixMode);
    const scene = it.scene;
    const sceneIdx = it.sceneIdx;
    const raw = it.raw;

    const heading = parseSceneHeadingParts(scene?.title || "");

    const { dataUrl, type } = inferImageInfo(sh);
    const imageName = dataUrl
      ? makeShotImageFilename(sceneIdx, sh.shotNumber, type)
      : "";

    rows.push([
      it.sceneLabel ?? sceneIdx,
      shotDisplayNumber,
      heading.intExt,
      heading.location,
      heading.timeOfDay,
      // ✅ tag values translated per selected language
      displayTagValue("shotSize", sh.shotSize || "", lang),
      displayTagValue("angle", sh.angle || sh.shotType || "", lang),
      (sh.lensMm || "").trim(),
      displayTagValue("focus", sh.focus || "", lang),
      displayTagValue("framing", sh.framing || "", lang),
      displayTagValue("movement", sh.movement || "", lang),
      (sh.lights || "").trim(),
      safeInt(sh.durationEstimateSec, 0),
      (sh.description || "").trim(),
      getShotFrameFormat(effectiveFrameFormatValue(sh, scene, shotFrameFormat)).short,
      t(getShotColorFormat(effectiveColorFormatValue(sh, scene)).labelKey),
      imageName,
      (raw || "").trim(),
      sh.isMaster ? "MASTER" : "",
    ]);
  }

  const eol = "\r\n";
  const body = rows
    .map((cols) => cols.map((c) => csvCell(c, delimiter)).join(delimiter))
    .join(eol);

  return { filename, csv: includeBOM ? `\ufeff${body}` : body };
}
